// src/features/links/LinkSearchField.tsx
// IR-1386 r2 (Rotem) — the Search box moved OUT of the filter grid and into the
// sort row, first in line. It was the 1st of 9 dropdown-shaped controls in the
// filter bar, which made a free-text field read as one more dropdown; on the
// sort row it is the only text input and sits where the eye starts.
// The input + its IR-1233 match navigation moved here verbatim so the Ctrl+F
// semantics (Enter = next, Shift+Enter = previous) are unchanged.
export interface MatchNav {
  /** 1-based index of the active match; 0 = nothing active yet. */
  current: number;
  /**
   * IR-1233 r6 — ALL matching rows, straight off the backend `total`: the same
   * number as the "X / Y links" title. Counting rendered <mark> elements
   * instead only sees the loaded page.
   */
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

interface Props {
  /** Current query (`LinksFilters.q`); "" when unset. */
  value: string;
  onChange: (q: string) => void;
  /** Omitted where match navigation isn't wired — then no n/m UI renders. */
  matchNav?: MatchNav | undefined;
}

export function LinkSearchField({ value, onChange, matchNav }: Props) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-[var(--iris-fg-3)]">Search</span>
      <input
        aria-label="Search"
        placeholder="Search link text…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={
          matchNav
            ? (e) => {
                // IR-1233 r3 — Ctrl+F semantics: Enter = next match,
                // Shift+Enter = previous match.
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (e.shiftKey) matchNav.onPrev();
                else matchNav.onNext();
              }
            : undefined
        }
        className="iris-input w-56 text-sm"
      />
      {matchNav && value && (
        <>
          <span
            className="text-xs tabular-nums text-[var(--iris-fg-3)]"
            aria-label={`Match ${matchNav.current} of ${matchNav.total}`}
          >
            {matchNav.current}/{matchNav.total}
          </span>
          <button
            type="button"
            className="iris-btn text-xs"
            aria-label="Previous match"
            disabled={matchNav.total === 0}
            onClick={matchNav.onPrev}
          >
            ↑
          </button>
          <button
            type="button"
            className="iris-btn text-xs"
            aria-label="Next match"
            disabled={matchNav.total === 0}
            onClick={matchNav.onNext}
          >
            ↓
          </button>
        </>
      )}
    </div>
  );
}
